import React, { forwardRef } from 'react';
import { FaSearch, FaTimes, FaChevronUp, FaChevronDown, FaSpinner } from 'react-icons/fa';

// A Chrome-find-style bar for data tables. The parent owns the results/index
// state; this component is pure UI + keyboard plumbing.
const FindBar = forwardRef(
  (
    {
      value,
      onChange,
      onSubmit, // fired on Enter when value has changed since last search
      onNext,
      onPrev,
      onClear,
      totalMatches, // null when nothing has been searched yet
      currentIndex, // 0-based index of the active match
      loading,
      capped,
      lastSearchedTerm,
      errorMessage,
      placeholder = 'Find in results',
    },
    ref
  ) => {
    const hasResults = totalMatches !== null && totalMatches > 0;
    const noResults = totalMatches === 0 && lastSearchedTerm && !loading;
    const trimmed = (value || '').trim();
    const isSameAsLast = lastSearchedTerm !== null && lastSearchedTerm === trimmed;

    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (e.shiftKey) {
          if (hasResults) onPrev();
          return;
        }
        if (hasResults && isSameAsLast) {
          onNext();
        } else if (trimmed) {
          onSubmit(trimmed);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClear();
      }
    };

    return (
      <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 bg-surface focus-within:bg-primary-100/70 focus-within:border-primary-300 transition-colors duration-200">
        <div className="relative flex-1 max-w-md group transition-transform duration-200 focus-within:scale-[1.015] origin-left">
          {/* One-shot expanding ring — fires each time the input gains focus */}
          <span
            aria-hidden
            className="pointer-events-none absolute -inset-0.5 rounded-md ring-2 ring-primary-500 opacity-0 group-focus-within:animate-findPulse"
          />
          <span className="absolute inset-y-0 left-2.5 flex items-center pointer-events-none text-slate-400 group-focus-within:text-primary-600 transition-colors">
            {loading ? (
              <FaSpinner className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <FaSearch className="w-3.5 h-3.5" />
            )}
          </span>
          <input
            ref={ref}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="w-full pl-8 pr-24 py-1.5 text-xs bg-surface border border-slate-300 rounded-md placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-primary-500/40 focus:border-primary-500 focus:shadow-[0_0_18px_rgb(var(--accent-rgb)/0.35)] transition-all duration-150"
          />
          {/* Counter + controls, absolute-positioned inside the input */}
          <div className="absolute inset-y-0 right-1 flex items-center gap-0.5">
            {trimmed && (
              <span className="text-[10px] tabular-nums select-none mr-1 whitespace-nowrap px-1.5">
                {loading ? (
                  <span className="text-slate-400">Searching…</span>
                ) : errorMessage ? (
                  <span className="text-red-500">Error</span>
                ) : totalMatches === null ? (
                  <button
                    type="button"
                    onClick={() => onSubmit(trimmed)}
                    className="text-primary-600 hover:text-primary-800 font-medium cursor-pointer hover:underline transition-colors"
                  >
                    Search
                  </button>
                ) : totalMatches === 0 ? (
                  <span className="text-red-500">0 matches</span>
                ) : (
                  <span className="text-slate-600">
                    <span className="font-semibold text-slate-800">{currentIndex + 1}</span>
                    <span className="text-slate-400"> / </span>
                    <span className="text-slate-700">
                      {totalMatches}
                      {capped ? '+' : ''}
                    </span>
                  </span>
                )}
              </span>
            )}
            <button
              type="button"
              onClick={onPrev}
              disabled={!hasResults}
              title="Previous match (Shift+Enter)"
              className="inline-flex items-center justify-center w-6 h-6 rounded text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors"
            >
              <FaChevronUp className="w-2.5 h-2.5" />
            </button>
            <button
              type="button"
              onClick={onNext}
              disabled={!hasResults}
              title="Next match (Enter)"
              className="inline-flex items-center justify-center w-6 h-6 rounded text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors"
            >
              <FaChevronDown className="w-2.5 h-2.5" />
            </button>
            {(trimmed || hasResults || noResults) && (
              <button
                type="button"
                onClick={onClear}
                title="Clear (Esc)"
                className="inline-flex items-center justify-center w-6 h-6 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
              >
                <FaTimes className="w-2.5 h-2.5" />
              </button>
            )}
          </div>
        </div>
        {capped && hasResults && (
          <span className="text-[10px] text-amber-600 tracking-wide">
            Showing first 500 matches — refine filters to narrow.
          </span>
        )}
        {errorMessage && (
          <span className="text-[11px] text-red-600 truncate" title={errorMessage}>
            {errorMessage}
          </span>
        )}
        <span className="ml-auto text-[10px] uppercase tracking-[0.1em] text-slate-400 hidden sm:inline">
          Ctrl/⌘ + F
        </span>
      </div>
    );
  }
);

FindBar.displayName = 'FindBar';

export default FindBar;
